import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useEffect, useMemo } from 'react';

import { type AudienceActivationType, type AudienceType } from '@/api/audience';
import {
  type IntegrationProviderType,
  useIntegrationProvidersByCapability,
} from '@/api/integration';
import { useCurrentInstance } from '@/api/organization';
import {
  getActivationRequirements,
  isActivationAllowed,
} from '@/app/audiences/audience-activation-util';
import AudienceActivationProviderChooser from '@/app/audiences/AudienceActivationProviderChooser';
import { LinkButton } from '@/components/buttons';
import WidgetError from '@/components/errors/WidgetError';
import { useAppForm } from '@/components/Form';
import { Spinner } from '@/components/loaders';
import { Results } from '@/components/Results';
import { Text } from '@/components/typography';
import { useWizardContext } from '@/components/wizard';
import { ROUTES } from '@/router';

type Props = {
  audience: AudienceType;
};

const AudienceActivationSelectVendorStep = ({ audience }: Props) => {
  const { instance } = useCurrentInstance();
  const { registerOnComplete, updateWizardData } = useWizardContext<{
    activation: AudienceActivationType;
  }>();
  const { providers, isLoading, error } = useIntegrationProvidersByCapability('audienceActivation');
  const form = useAppForm({
    defaultValues: {
      activation: {
        provider: undefined as IntegrationProviderType | undefined,
      },
    },
  });

  const validateProvider = (provider?: IntegrationProviderType) => {
    if (!provider) {
      return { message: t`Please select a vendor to activate this audience` };
    }

    if (isActivationAllowed(audience, provider)) {
      return undefined;
    }

    const { audienceType, minSizeString, memberCount } = getActivationRequirements(
      audience,
      provider,
    );

    return {
      message: t`${provider.name} requires a minimum of ${minSizeString ?? '(unknown count)'} ${audienceType ?? '(unknown audience type)'} records to activate an audience. This audience only has ${memberCount ?? '(unknown count)'}. Please choose a different audience or update the current audience to include more members.`,
    };
  };

  useEffect(() => {
    registerOnComplete(async () => {
      if (isLoading || error) {
        return false;
      }

      const errors = await form.validateField('activation.provider', 'change');
      if (errors.length > 0) {
        return false;
      }

      const provider = form.getFieldValue('activation.provider');
      if (!provider) {
        return false;
      }

      updateWizardData({
        activation: {
          provider,
        } as AudienceActivationType,
      });

      return true;
    });
  }, [error, form, isLoading, registerOnComplete, updateWizardData]);

  const filteredProviders = useMemo(() => {
    return providers?.filter(
      (provider) =>
        !audience.activations?.some((activeProvider) => activeProvider.provider.id === provider.id),
    );
  }, [providers, audience.activations]);

  if (error) {
    return (
      <div className="flex items-center justify-center">
        <WidgetError />
      </div>
    );
  }

  if (providers && providers.length === 0) {
    return (
      <Results
        status="error"
        title={t`Add an audience activation vendor`}
        subtitle={t`You have not connected a vendor which supports audience activation. Visit Integrations to do this and then return and try again.`}
        extra={
          <LinkButton variant="primary" to={ROUTES.integrations.path}>
            <Trans>Go to Integrations</Trans>
          </LinkButton>
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-8">
      <Text variant="body1" weight="light">
        <Trans>
          Activate your "<strong>{audience.name}</strong>" audience by selecting one of the vendors
          below:
        </Trans>
      </Text>
      {isLoading ? (
        <Spinner className="mx-auto" variant="spoke" />
      ) : (
        <form.AppForm>
          <form.AppField
            name="activation.provider"
            validators={{
              onChange: ({ value }) => validateProvider(value),
            }}
          >
            {(field) => (
              <field.Field>
                <AudienceActivationProviderChooser
                  audience={audience}
                  hasActiveCRMConnection={instance?.hasActiveCRMConnection}
                  options={filteredProviders ?? []}
                  value={field.state.value}
                  onChange={field.handleChange}
                  onBlur={field.handleBlur}
                />
                <field.Error />
              </field.Field>
            )}
          </form.AppField>
        </form.AppForm>
      )}
    </div>
  );
};

export default AudienceActivationSelectVendorStep;
